import { useState, useEffect, useRef } from 'react';
import { TrendingUp, Flame, Trophy, ChevronDown } from 'lucide-react';
import NutritionHeatMap from './NutritionHeatMap';
import { supabase } from '../lib/supabase';

type MacroType = 'overall' | 'protein' | 'carbs' | 'fat';

interface DayData {
  date: string;
  percentage: number;
  actual: number;
  goal: number;
}

interface StreakData {
  current: number;
  longest: number;
}

interface NutritionStreakCardProps {
  userId: string;
}

export default function NutritionStreakCard({ userId }: NutritionStreakCardProps) {
  const [selectedMacro, setSelectedMacro] = useState<MacroType>('overall');
  const [selectedMonthOffset, setSelectedMonthOffset] = useState<number>(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [heatMapData, setHeatMapData] = useState<Record<MacroType, DayData[]>>({
    overall: [],
    protein: [],
    carbs: [],
    fat: [],
  });
  const [streakData, setStreakData] = useState<Record<MacroType, StreakData>>({
    overall: { current: 0, longest: 0 },
    protein: { current: 0, longest: 0 },
    carbs: { current: 0, longest: 0 },
    fat: { current: 0, longest: 0 },
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadNutritionData();

    if (selectedMonthOffset === 0) {
      const interval = setInterval(() => {
        loadNutritionData();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [userId, selectedMonthOffset]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const loadNutritionData = async () => {
    try {
      const now = new Date();
      const targetDate = new Date(now.getFullYear(), now.getMonth() - selectedMonthOffset, 1);

      const startDate = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
      startDate.setHours(0, 0, 0, 0);

      let endDate: Date;
      if (selectedMonthOffset === 0) {
        endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
      } else {
        endDate = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
        endDate.setHours(23, 59, 59, 999);
      }

      // ============================================
      // CHANGED: Get food logging data for "Overall" tab
      // ============================================
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      const { data: foodLogsData, error: foodLogsError } = await supabase
        .from('food_logs')
        .select('date')
        .eq('user_id', userId)
        .gte('date', startDateStr)
        .lte('date', endDateStr);

      if (foodLogsError) {
        console.error('Error loading food logs:', foodLogsError);
      }

      // Create a set of dates with food logs
      const foodLogDates = new Set<string>();
      if (foodLogsData) {
        foodLogsData.forEach((log: any) => {
          if (log.date) {
            foodLogDates.add(log.date);
          }
        });
      }

      // Build overall data based on food logging
      const overallCompletionData: DayData[] = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const lastDayOfMonth = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
      
      for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
        const currentDate = new Date(targetDate.getFullYear(), targetDate.getMonth(), day);
        const dateStr = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        
        if (currentDate <= today) {
          const hasLoggedFood = foodLogDates.has(dateStr);
          overallCompletionData.push({
            date: dateStr,
            percentage: hasLoggedFood ? 100 : 0,
            actual: hasLoggedFood ? 1 : 0,
            goal: 1,
          });
        }
      }

      // ============================================
      // ORIGINAL: Get macro nutrition data (protein, carbs, fat)
      // ============================================
      const { data: nutritionData, error } = await supabase.rpc(
        'get_daily_nutrition_totals',
        {
          p_user_id: userId,
          p_start_date: startDate.toISOString().split('T')[0],
          p_end_date: endDate.toISOString().split('T')[0],
        }
      );

      if (error) {
        console.error('Error loading nutrition data:', error);
        return;
      }

      if (nutritionData) {
        const proteinData: DayData[] = [];
        const carbsData: DayData[] = [];
        const fatData: DayData[] = [];

        nutritionData.forEach((day: any) => {
          proteinData.push({
            date: day.date,
            percentage: day.protein_percentage || 0,
            actual: day.total_protein || 0,
            goal: day.protein_goal || 0,
          });

          carbsData.push({
            date: day.date,
            percentage: day.carbs_percentage || 0,
            actual: day.total_carbs || 0,
            goal: day.carbs_goal || 0,
          });

          fatData.push({
            date: day.date,
            percentage: day.fat_percentage || 0,
            actual: day.total_fat || 0,
            goal: day.fat_goal || 0,
          });
        });

        setHeatMapData({
          overall: overallCompletionData,
          protein: proteinData,
          carbs: carbsData,
          fat: fatData,
        });
      }

      // ============================================
      // ORIGINAL: Streak calculations
      // ============================================
      const overallStreakResult = await supabase.rpc('calculate_overall_completion_streak', {
        p_user_id: userId,
      });

      const proteinStreakResult = await supabase.rpc('calculate_macro_streak', {
        p_user_id: userId,
        p_macro_type: 'protein',
      });

      const carbsStreakResult = await supabase.rpc('calculate_macro_streak', {
        p_user_id: userId,
        p_macro_type: 'carbs',
      });

      const fatStreakResult = await supabase.rpc('calculate_macro_streak', {
        p_user_id: userId,
        p_macro_type: 'fat',
      });

      setStreakData({
        overall: {
          current: overallStreakResult.data?.[0]?.current_streak || 0,
          longest: overallStreakResult.data?.[0]?.longest_streak || 0,
        },
        protein: {
          current: proteinStreakResult.data?.[0]?.current_streak || 0,
          longest: proteinStreakResult.data?.[0]?.longest_streak || 0,
        },
        carbs: {
          current: carbsStreakResult.data?.[0]?.current_streak || 0,
          longest: carbsStreakResult.data?.[0]?.longest_streak || 0,
        },
        fat: {
          current: fatStreakResult.data?.[0]?.current_streak || 0,
          longest: fatStreakResult.data?.[0]?.longest_streak || 0,
        },
      });
    } catch (error) {
      console.error('Error loading nutrition data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getMacroColor = (macro: MacroType): string => {
    const colors = {
      overall: 'green',
      protein: 'blue',
      carbs: 'orange',
      fat: 'yellow',
    };
    return colors[macro];
  };

  const getMacroLabel = (macro: MacroType): string => {
    const labels = {
      overall: 'Overall Daily',
      protein: 'Protein',
      carbs: 'Carbs',
      fat: 'Fats',
    };
    return labels[macro];
  };

  const getButtonStyles = (macro: MacroType): string => {
    const isSelected = selectedMacro === macro;
    const color = getMacroColor(macro);

    const styles = {
      overall: isSelected
        ? 'bg-green-600 text-white'
        : 'bg-white text-gray-700 hover:bg-green-50',
      protein: isSelected
        ? 'bg-blue-600 text-white'
        : 'bg-white text-gray-700 hover:bg-blue-50',
      carbs: isSelected
        ? 'bg-orange-600 text-white'
        : 'bg-white text-gray-700 hover:bg-orange-50',
      fat: isSelected
        ? 'bg-yellow-600 text-white'
        : 'bg-white text-gray-700 hover:bg-yellow-50',
    };

    return styles[macro];
  };

  const getCurrentWeekData = (): DayData[] => {
    const data = heatMapData[selectedMacro];
    return data.slice(0, 7);
  };

  const getWeeklyCompletion = (): number => {
    const weekData = getCurrentWeekData();
    return weekData.filter((day) => day.percentage >= 100).length;
  };

  const getAverageIntake = (): { actual: number; goal: number } => {
    const data = heatMapData[selectedMacro];
    if (data.length === 0) return { actual: 0, goal: 0 };

    const totalActual = data.reduce((sum, day) => sum + day.actual, 0);
    const totalGoal = data.reduce((sum, day) => sum + day.goal, 0);

    return {
      actual: totalActual / data.length,
      goal: totalGoal / data.length,
    };
  };

  const getMonthLabel = (offset: number): string => {
    const now = new Date();
    const targetDate = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    return targetDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-5 border border-gray-200 animate-pulse">
        <div className="h-8 bg-gray-200 rounded-lg mb-4"></div>
        <div className="h-32 bg-gray-200 rounded-lg mb-4"></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="h-20 bg-gray-200 rounded-lg"></div>
          <div className="h-20 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  const currentData = heatMapData[selectedMacro];
  const currentStreak = streakData[selectedMacro];
  const averages = getAverageIntake();

  const getTargetYearMonth = () => {
    const now = new Date();
    const targetDate = new Date(now.getFullYear(), now.getMonth() - selectedMonthOffset, 1);
    return {
      year: targetDate.getFullYear(),
      month: targetDate.getMonth(),
    };
  };

  const { year: targetYear, month: targetMonth } = getTargetYearMonth();

  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-200">
      <div className="mb-4 relative" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="w-full px-4 py-3 bg-gradient-to-br from-cyan-50 to-blue-50 border-2 border-cyan-200 rounded-xl font-bold text-sm text-gray-800 hover:from-cyan-100 hover:to-blue-100 transition-all cursor-pointer flex items-center justify-between active:scale-[0.98] touch-manipulation"
        >
          <span>{getMonthLabel(selectedMonthOffset)} {selectedMonthOffset === 0 && '(Current)'}</span>
          <ChevronDown
            size={18}
            strokeWidth={2.5}
            className={`text-cyan-600 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isDropdownOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-cyan-200 rounded-xl shadow-xl overflow-hidden z-50 animate-slideDown">
            {[0, 1, 2].map((offset) => (
              <button
                key={offset}
                onClick={() => {
                  setSelectedMonthOffset(offset);
                  setIsDropdownOpen(false);
                }}
                className={`w-full px-4 py-3 text-left text-sm font-semibold transition-colors touch-manipulation ${
                  selectedMonthOffset === offset
                    ? 'bg-cyan-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-cyan-50 active:bg-cyan-100'
                }`}
              >
                {getMonthLabel(offset)} {offset === 0 && '(Current)'}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-4 gap-2 mb-5">
        <button
          onClick={() => setSelectedMacro('overall')}
          className={`py-2.5 rounded-xl font-bold text-xs transition-all ${getButtonStyles('overall')}`}
        >
          Overall
        </button>
        <button
          onClick={() => setSelectedMacro('protein')}
          className={`py-2.5 rounded-xl font-bold text-xs transition-all ${getButtonStyles('protein')}`}
        >
          Protein
        </button>
        <button
          onClick={() => setSelectedMacro('carbs')}
          className={`py-2.5 rounded-xl font-bold text-xs transition-all ${getButtonStyles('carbs')}`}
        >
          Carbs
        </button>
        <button
          onClick={() => setSelectedMacro('fat')}
          className={`py-2.5 rounded-xl font-bold text-xs transition-all ${getButtonStyles('fat')}`}
        >
          Fats
        </button>
      </div>

      <div>
        <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-4">
          {getMacroLabel(selectedMacro)} Tracking - {getMonthLabel(selectedMonthOffset)}
        </p>
        <NutritionHeatMap
          data={currentData}
          macroType={selectedMacro}
          year={targetYear}
          month={targetMonth}
        />
      </div>
    </div>
  );
}
